import {
  Divider,
  GKButton,
  Grid2,
  PopConfirm,
  Show,
  Tab,
  Tabs,
} from "components";
import {
  HoldingDataInterface,
  PortfolioDetailsInterface,
} from "interfaces/Portfolio";
import React, { lazy, memo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { deletePortfolio } from "store/slices/portfolioSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { UserRoleTypes } from "utils/common";

const ModelPortfolioTab = lazy(
  () => import("./PortfolioBaskets/ModelPortfolioTab"),
);
const PortfolioNotes = lazy(() => import("./PortfolioNotes"));
const PortfolioPreview = lazy(() => import("./PortfolioPreview"));
const PortfolioProfileTab = lazy(() => import("./PortfolioProfileTab"));
const PortfolioRevenue = lazy(() => import("./PortfolioRevenue"));
const ReportsPages = lazy(() => import("./ReportsPages"));
const PortfolioSettingsEdit = lazy(() => import("./SettingsPages"));
const SettingTab = lazy(() => import("./SettingTab"));
const Benchmark = lazy(() => import("./SummaryPages/Benchmark"));
const PortfolioUtilityEdit = lazy(() => import("./UtilityPages"));

interface Props {
  portfolioDetailData: PortfolioDetailsInterface;
  holdingSummaryDetails: HoldingDataInterface;
}

const PortfolioAdminViewTab = memo((props: Props) => {
  const { portfolioDetailData, holdingSummaryDetails } = props;

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const userData = useAppSelector((state) => state?.user?.userData);
  const { portfolioAdminViewTabs } = useAppSelector((state) => state?.app?.tab);
  const { loading } = useAppSelector((state) => state?.portfolio);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, value: number) => {
    dispatch(TAB({ tabKey: "portfolioAdminViewTabs", tabValue: value }));
  };

  const tabsConfig = [
    {
      label: "Profile",
      value: 0,
      component: (
        <PortfolioProfileTab portfolioDetailData={portfolioDetailData} />
      ),
      hideForClient: true,
    },
    {
      label: "Imports",
      value: 1,
      component: <PortfolioUtilityEdit />,
      hideForClient: true,
    },
    {
      label: "Reports",
      value: 2,
      component: <ReportsPages portfolioDetailData={portfolioDetailData} />,
      hideForClient: true,
    },
    {
      label: "Review",
      value: 3,
      component: (
        <PortfolioPreview
          portfolioDetailData={portfolioDetailData}
          holdingSummaryDetails={holdingSummaryDetails}
        />
      ),
      hideForClient: true,
    },
    {
      label: "Notes",
      value: 4,
      component: <PortfolioNotes portfolioDetailData={portfolioDetailData} />,
      hideForClient: true,
    },
    {
      label: "Benchmark",
      value: 5,
      component: <Benchmark benchmarkId={portfolioDetailData?.benchmark} />,
      hideForClient: true,
    },
    {
      label: "Baskets",
      value: 6,
      component: (
        <ModelPortfolioTab
          holdingSummaryDetails={holdingSummaryDetails}
          portfolioDetailData={portfolioDetailData}
        />
      ),
      hideForClient: true,
    },
    {
      label: "Revenue",
      value: 7,
      component: <PortfolioRevenue portfolioDetailData={portfolioDetailData} />,
      hideForClient: true,
    },
    {
      label: "Access",
      value: 8,
      component: <PortfolioSettingsEdit />,
      hideForClient: true,
    },
    {
      label: "Settings",
      value: 9,
      component: <SettingTab data={portfolioDetailData} />,
      hideForClient: true,
    },
  ];

  const filteredTabs = tabsConfig.filter(
    (tab) =>
      !(tab.hideForClient && userData?.userRole === UserRoleTypes.client),
  );

  return (
    <>
      <Grid2 size={12} container>
        <Grid2 size={8}>
          <Tabs
            value={portfolioAdminViewTabs}
            onChange={handleTabChange}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons={false}
          >
            {filteredTabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Grid2>
        <Grid2 size={4} textAlign={"right"}>
          <Show.When isTrue={portfolioAdminViewTabs === 9}>
            <GKButton
              loading={loading}
              variant="outlined"
              color="error"
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              Delete Portfolio
            </GKButton>
          </Show.When>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>
      </Grid2>

      <Grid2 size={12}>
        {filteredTabs.map(
          (tabConfig) =>
            portfolioAdminViewTabs === tabConfig.value && (
              <Show.When key={tabConfig.value} isTrue>
                {tabConfig.component}
              </Show.When>
            ),
        )}
      </Grid2>

      {Boolean(anchorEl) && (
        <PopConfirm
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          setOpen={setAnchorEl}
          onSuccess={() => {
            dispatch(deletePortfolio(id)).then(() => navigate(`/app/holdings`));
          }}
        />
      )}
    </>
  );
});

export default PortfolioAdminViewTab;
